import { ICharacteristicFunctionInput } from "@kuika/kuika-cl-model";

/**
 * Legacy `EDataType` ordinal -> type name.
 *
 * Deliberately a copy of the backend's `Kuika.Common/Json/EDataTypeToStringConverter` table
 * (including `7 -> DateTime`, where the C# enum aliases `Date` and `DateTime` to the same
 * ordinal), so a legacy record resolves to the same name on both sides. A number outside the
 * table falls back to "String" for the same reason.
 */
const LEGACY_E_DATA_TYPE_NAMES: Record<number, string> = {
  1: "String",
  2: "Boolean",
  3: "Int32",
  4: "Int64",
  5: "Decimal",
  6: "Double",
  7: "DateTime",
  8: "DateTimeOffset",
  9: "Guid",
  10: "Image",
  11: "FileContent",
  12: "Location",
  13: "Signature"
};

/**
 * Read-repair for method-input `type` on deserialized Bluetooth methods.
 *
 * `IInputOutput.type` is a type NAME ("String", "Int32") — that is what the symbol picker's
 * conversion table and `ScreenDesignHelper.getAreTypesCompatible` match on. Bluetooth method
 * inputs saved before this repair carry the numeric `EDataType` ordinal instead: the BLE
 * provider converted the name to the enum value on write, because the backend modelled
 * `ApiManagementParameterModel.DataType` as an `EDataType` enum until it moved to `string`
 * (+ a back-compat converter). The write side is gone now, but existing projects still hold
 * the ordinals, and a number reaching `getAreTypesCompatible` hits `.includes()` and throws —
 * which kills the whole symbol picker for that method, not just this input.
 *
 * Numeric-looking STRINGS are left alone on purpose: the backend converter treats a string
 * token as the type name as-is, and diverging here would resolve the same record differently
 * on the two sides.
 *
 * Mutates in place. Inputs are flat (the BLE method-input form offers primitive types only),
 * so there is nothing to recurse into.
 */
export function normalizeMethodInputTypes(inputs: ICharacteristicFunctionInput[] | undefined): void {
  if (!inputs) return;
  inputs.forEach((input: ICharacteristicFunctionInput): void => {
    const rawType: unknown = input.type;
    if (typeof rawType === "number") {
      input.type = LEGACY_E_DATA_TYPE_NAMES[rawType] ?? "String";
    }
  });
}
